import { useEffect, useState } from "react";
import {
    Loader2,
    Eye,
    Users,
    Download as DownloadIcon,
    AlertTriangle,
    RefreshCcw,
    Trash2,
    Image as ImageIcon,
    Archive as ArchiveIcon,
    Puzzle,
    Users2,
    HelpCircle,
    MapPin,
    Tag as TagIcon,
    Share2,
    BarChart3,
    ExternalLink,
    Activity,
    Trophy,
    Bell,
    Smartphone,
    Monitor,
    Tablet as TabletIcon,
    Chrome as ChromeIcon,
    ArrowUp,
    ArrowDown,
    Minus,
    Send,
} from "lucide-react";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import { api, formatApiErrorDetail, resolveMediaUrl } from "@/lib/api";
import { flagEmoji, countryName } from "@/lib/countries";

const REPORT_STATUS_LABELS = {
    pending: "Oczekujące",
    in_progress: "W trakcie",
    done: "Wykonane",
    cancelled: "Anulowane",
};
const REPORT_STATUS_COLORS = {
    pending: "#fbbf24",
    in_progress: "#60a5fa",
    done: "#34d399",
    cancelled: "#f87171",
};

export default function StatsManager() {
    const [stats, setStats] = useState(null);
    const [timeseries, setTimeseries] = useState([]);
    const [activeSeries, setActiveSeries] = useState({ data: [], bucket_seconds: 60, range: "24h" });
    const [activeRange, setActiveRange] = useState("24h");
    const [mapImage, setMapImage] = useState("");
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        setRefreshing(true);
        setError("");
        try {
            const [s, ts, mi, as_] = await Promise.all([
                api.get("/admin/stats"),
                api.get(`/admin/stats/timeseries?days=${days}`),
                api.get("/mod-info"),
                api.get(`/admin/stats/active-timeseries?range=${activeRange}`),
            ]);
            setStats(s.data);
            setTimeseries(ts.data || []);
            setMapImage(mi.data?.map_image_url || "");
            setActiveSeries(as_.data || { data: [], bucket_seconds: 60, range: activeRange });
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days, activeRange]);

    const onReset = async () => {
        if (
            !window.confirm(
                "Wyczyścić WSZYSTKIE dane statystyk (odwiedziny, pobrania)? Tej operacji nie da się cofnąć."
            )
        )
            return;
        try {
            await api.post("/admin/stats/reset");
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-3 text-[color:var(--text-secondary)]">
                <Loader2 className="animate-spin" size={18} /> Ładuję statystyki…
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="panel-yellow p-8 text-center text-sm text-[color:var(--text-secondary)]">
                <AlertTriangle
                    size={24}
                    className="mx-auto mb-3 text-[color:var(--primary)]"
                />
                {error || "Brak danych."}
            </div>
        );
    }

    const reports = stats.reports || { by_status: {} };
    const content = stats.content || {};

    return (
        <div data-testid="admin-stats" className="space-y-8">
            {/* Header / toolbar */}
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="label-eyebrow flex items-center gap-2">
                    <BarChart3 size={14} /> Statystyki witryny
                </div>
                <div className="flex items-center gap-2">
                    <select
                        data-testid="stats-range"
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value, 10))}
                        className="bg-[color:var(--bg-surface)] border border-white/10 text-white text-xs uppercase tracking-wider px-3 py-2 rounded-md"
                    >
                        <option value={7}>Ostatnie 7 dni</option>
                        <option value={14}>Ostatnie 14 dni</option>
                        <option value={30}>Ostatnie 30 dni</option>
                        <option value={90}>Ostatnie 90 dni</option>
                    </select>
                    <button
                        data-testid="stats-refresh"
                        onClick={load}
                        disabled={refreshing}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-[color:var(--text-secondary)] hover:text-white border border-white/10 rounded-md disabled:opacity-50"
                    >
                        <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} />
                        Odśwież
                    </button>
                    <button
                        data-testid="stats-reset"
                        onClick={onReset}
                        className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-red-400 hover:text-red-300 border border-red-400/30 rounded-md"
                    >
                        <Trash2 size={12} /> Wyczyść dane
                    </button>
                </div>
            </div>

            {error && (
                <div className="text-sm text-red-400" data-testid="stats-error">
                    {error}
                </div>
            )}

            {/* Top cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={Eye}
                    label="Wyświetlenia stron"
                    value={stats.pageviews?.total ?? 0}
                    sub={`Dziś: ${stats.pageviews?.today ?? 0} · 7d: ${
                        stats.pageviews?.last_7d ?? 0
                    } · 30d: ${stats.pageviews?.last_30d ?? 0}`}
                    delta={stats.pageviews?.delta_7d_pct}
                    color="#facc15"
                    testid="card-pageviews"
                />
                <StatCard
                    icon={Users}
                    label="Unikalni odwiedzający"
                    value={stats.unique_visitors?.total ?? 0}
                    sub={`7d: ${stats.unique_visitors?.last_7d ?? 0} · 30d: ${
                        stats.unique_visitors?.last_30d ?? 0
                    }`}
                    delta={stats.unique_visitors?.delta_7d_pct}
                    color="#60a5fa"
                    testid="card-unique"
                />
                <StatCard
                    icon={DownloadIcon}
                    label="Kliknięć Pobierz"
                    value={stats.downloads?.total ?? 0}
                    sub={`Dodatki: ${stats.downloads?.addons_total ?? 0} · 7d: ${
                        stats.downloads?.last_7d ?? 0
                    } · 30d: ${stats.downloads?.last_30d ?? 0}`}
                    delta={stats.downloads?.delta_7d_pct}
                    color="#34d399"
                    testid="card-downloads"
                />
                <StatCard
                    icon={AlertTriangle}
                    label="Zgłoszenia błędów"
                    value={reports.total ?? 0}
                    sub={`7d: ${reports.last_7d ?? 0} · 30d: ${reports.last_30d ?? 0}`}
                    color="#f97316"
                    testid="card-reports"
                />
            </div>

            {/* Push subscribers + broadcast panel */}
            <PushPanel stats={stats.push} onSent={load} />

            {/* Device / Browser / OS breakdown */}
            <div className="grid md:grid-cols-3 gap-4">
                <BreakdownCard
                    icon={Smartphone}
                    title="Urządzenia"
                    rows={stats.devices || []}
                    labelMap={{
                        mobile: "Telefon",
                        desktop: "Komputer",
                        tablet: "Tablet",
                        unknown: "Nieznane",
                        inne: "Inne",
                    }}
                    color="#facc15"
                    testid="breakdown-devices"
                />
                <BreakdownCard
                    icon={ChromeIcon}
                    title="Przeglądarki"
                    rows={stats.browsers || []}
                    color="#60a5fa"
                    testid="breakdown-browsers"
                />
                <BreakdownCard
                    icon={Monitor}
                    title="Systemy operacyjne"
                    rows={stats.operating_systems || []}
                    color="#a78bfa"
                    testid="breakdown-os"
                />
            </div>

            {/* Live now + All-time peak */}
            <div className="grid sm:grid-cols-2 gap-4">
                <div
                    data-testid="stats-live-now"
                    className="panel-yellow p-5 flex items-center gap-4"
                >
                    <span className="relative flex h-3 w-3 shrink-0">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-60" />
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-[#22c55e]" />
                    </span>
                    <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)]">
                            Aktywne sesje (na żywo)
                        </div>
                        <div className="text-2xl font-display text-white leading-none mt-0.5">
                            {stats.live_sessions ?? 0}
                        </div>
                        <div className="text-[10px] text-[color:var(--text-muted)] mt-1.5">
                            Unikalne sesje aktywne w ciągu ostatnich 2 minut.
                        </div>
                    </div>
                </div>

                <div
                    data-testid="stats-active-peak"
                    className="panel-yellow p-5 flex items-center gap-4"
                >
                    <span
                        className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
                        style={{ background: "#fbbf2422", color: "#fbbf24" }}
                    >
                        <Trophy size={18} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)]">
                            Rekord osób online (all-time)
                        </div>
                        <div className="text-2xl font-display text-white leading-none mt-0.5">
                            {stats.active_peak?.count ?? 0}
                        </div>
                        <div className="text-[10px] text-[color:var(--text-muted)] mt-1.5 truncate">
                            {stats.active_peak?.at
                                ? `Osiągnięto: ${formatPeakTs(stats.active_peak.at)}`
                                : "Brak danych - rekord pojawi się gdy ktoś wejdzie na stronę."}
                        </div>
                    </div>
                </div>
            </div>

            {/* Timeseries chart */}
            <div className="panel-yellow p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="label-eyebrow">
                        Ruch w czasie ({days} dni)
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                        <Legend color="#facc15" label="Wyświetlenia" />
                        <Legend color="#34d399" label="Pobrania" />
                    </div>
                </div>
                <div data-testid="stats-chart" style={{ width: "100%", height: 260 }}>
                    <ResponsiveContainer>
                        <AreaChart data={timeseries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="gradPv" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#facc15" stopOpacity={0.5} />
                                    <stop offset="95%" stopColor="#facc15" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="gradDl" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#34d399" stopOpacity={0.5} />
                                    <stop offset="95%" stopColor="#34d399" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                            <XAxis
                                dataKey="day"
                                tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                                tickFormatter={(d) => (d ? d.slice(5) : "")}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                                width={28}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: "#0b1220",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    fontSize: 12,
                                }}
                                labelStyle={{ color: "#facc15" }}
                            />
                            <Area
                                type="monotone"
                                dataKey="pageviews"
                                stroke="#facc15"
                                fill="url(#gradPv)"
                                strokeWidth={2}
                                name="Wyświetlenia"
                            />
                            <Area
                                type="monotone"
                                dataKey="downloads"
                                stroke="#34d399"
                                fill="url(#gradDl)"
                                strokeWidth={2}
                                name="Pobrania"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Active users (concurrent) timeseries chart */}
            <div className="panel-yellow p-6" data-testid="stats-active-chart-panel">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="label-eyebrow flex items-center gap-2">
                            <Activity size={14} /> Osoby online w czasie
                        </div>
                        <div className="text-[11px] text-[color:var(--text-muted)] mt-1">
                            Maksymalna liczba jednoczesnych aktywnych sesji w każdej{" "}
                            {activeBucketLabel(activeSeries.bucket_seconds)}.
                        </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                        {[
                            { v: "1h", label: "1H" },
                            { v: "6h", label: "6H" },
                            { v: "24h", label: "24H" },
                            { v: "7d", label: "7D" },
                            { v: "30d", label: "30D" },
                        ].map((opt) => (
                            <button
                                key={opt.v}
                                data-testid={`stats-active-range-${opt.v}`}
                                onClick={() => setActiveRange(opt.v)}
                                className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-md border transition-colors ${
                                    activeRange === opt.v
                                        ? "bg-[color:var(--primary)] text-[color:var(--bg-base)] border-[color:var(--primary)]"
                                        : "text-[color:var(--text-secondary)] border-white/10 hover:text-white hover:border-white/30"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div data-testid="stats-active-chart" style={{ width: "100%", height: 240 }}>
                    {(activeSeries.data || []).length === 0 ? (
                        <Empty text="Brak danych - wykres zacznie wypełniać się gdy ktoś odwiedzi stronę." />
                    ) : (
                        <ResponsiveContainer>
                            <AreaChart
                                data={activeSeries.data}
                                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="gradActive" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.6} />
                                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                                <XAxis
                                    dataKey="ts"
                                    tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                                    tickFormatter={(t) => formatTick(t, activeSeries.bucket_seconds)}
                                    minTickGap={40}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 10 }}
                                    width={28}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "#0b1220",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        fontSize: 12,
                                    }}
                                    labelStyle={{ color: "#22c55e" }}
                                    labelFormatter={(t) => formatTooltipTs(t)}
                                    formatter={(v) => [v, "Osoby online"]}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="count"
                                    stroke="#22c55e"
                                    fill="url(#gradActive)"
                                    strokeWidth={2}
                                    name="Osoby online"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* Two columns: top pages & referrers */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="panel-yellow p-6" data-testid="stats-top-pages">
                    <div className="label-eyebrow mb-4">Najpopularniejsze strony</div>
                    {(stats.top_pages || []).length === 0 ? (
                        <Empty text="Brak danych - nikt jeszcze nie odwiedził strony." />
                    ) : (
                        <ul className="space-y-2">
                            {stats.top_pages.map((row) => (
                                <RankRow
                                    key={row.path}
                                    label={row.path}
                                    value={row.count}
                                    max={stats.top_pages[0].count}
                                />
                            ))}
                        </ul>
                    )}
                </div>

                <div className="panel-yellow p-6" data-testid="stats-top-referrers">
                    <div className="label-eyebrow mb-4">Źródła ruchu (top referrers)</div>
                    {(stats.top_referrers || []).length === 0 ? (
                        <Empty text="Brak zarejestrowanych zewnętrznych źródeł ruchu." />
                    ) : (
                        <ul className="space-y-2">
                            {stats.top_referrers.map((row) => (
                                <RankRow
                                    key={row.referrer}
                                    label={
                                        <a
                                            href={row.referrer}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:text-white inline-flex items-center gap-1"
                                        >
                                            {prettyHost(row.referrer)}
                                            <ExternalLink size={10} />
                                        </a>
                                    }
                                    value={row.count}
                                    max={stats.top_referrers[0].count}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Countries */}
            <div className="panel-yellow p-6" data-testid="stats-countries">
                <div className="flex items-center justify-between mb-4">
                    <div className="label-eyebrow">Kraje odwiedzających</div>
                    {stats.unknown_country_pageviews > 0 && (
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                            Nieznane: {stats.unknown_country_pageviews}
                        </span>
                    )}
                </div>
                {(stats.top_countries || []).length === 0 ? (
                    <Empty text="Brak danych - żaden publiczny IP nie został jeszcze rozpoznany." />
                ) : (
                    <div className="grid sm:grid-cols-2 gap-2">
                        {stats.top_countries.map((row) => {
                            const max = stats.top_countries[0].count || 1;
                            const pct = Math.max(4, (row.count / max) * 100);
                            return (
                                <div
                                    key={row.code}
                                    data-testid={`stats-country-${row.code}`}
                                    className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-white/[0.02] border border-white/5"
                                >
                                    <span
                                        className="text-2xl leading-none"
                                        aria-hidden="true"
                                    >
                                        {flagEmoji(row.code)}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <span className="text-sm text-white truncate">
                                                {countryName(row.code, row.name)}
                                            </span>
                                            <span className="font-mono text-[11px] text-[color:var(--text-secondary)] shrink-0">
                                                <span className="text-[color:var(--primary)]">
                                                    {row.count}
                                                </span>
                                                <span className="opacity-50">
                                                    {" · "}
                                                </span>
                                                {row.unique} uniq
                                            </span>
                                        </div>
                                        <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                                            <div
                                                className="h-full bg-[color:var(--primary)]/70"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="text-[10px] text-[color:var(--text-muted)] mt-3">
                    Lokalizacja na podstawie publicznego adresu IP (geojs.io /
                    ip-api.com), dane buforowane przez 7 dni. Nie zapisujemy
                    surowych adresów IP.
                </div>
            </div>

            {/* POI heatmap */}
            <div className="panel-yellow p-6" data-testid="stats-poi-heatmap">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="label-eyebrow">
                            Heatmapa kliknięć POI
                        </div>
                        <div className="text-[11px] text-[color:var(--text-muted)] mt-1">
                            Łącznie kliknięć POI:{" "}
                            <span className="text-[color:var(--primary)]">
                                {stats.poi_clicks_total ?? 0}
                            </span>
                            {(stats.top_pois || []).length > 0 &&
                                ` · ${stats.top_pois.length} miejsc wyróżnionych`}
                        </div>
                    </div>
                </div>
                {(stats.top_pois || []).length === 0 ? (
                    <Empty text="Brak kliknięć POI - kliknięcia w pinezki na interaktywnej mapie pojawią się tutaj." />
                ) : (
                    <div className="grid lg:grid-cols-12 gap-4 items-start">
                        <div className="lg:col-span-8">
                            {mapImage ? (
                                <div className="relative rounded-md overflow-hidden border border-white/5 bg-[color:var(--bg-base)]">
                                    <img
                                        src={resolveMediaUrl(mapImage)}
                                        alt="Heatmapa mapy"
                                        className="w-full h-auto block opacity-60"
                                    />
                                    {(() => {
                                        const max = Math.max(
                                            ...stats.top_pois.map((p) => p.count || 1),
                                            1,
                                        );
                                        return stats.top_pois.map((p) => {
                                            const intensity =
                                                Math.log(p.count + 1) /
                                                Math.log(max + 1);
                                            const size = 14 + intensity * 36;
                                            return (
                                                <div
                                                    key={p.id}
                                                    data-testid={`heatmap-dot-${p.id}`}
                                                    title={`${p.title} - ${p.count}`}
                                                    className="absolute pointer-events-none"
                                                    style={{
                                                        left: `${p.x}%`,
                                                        top: `${p.y}%`,
                                                        width: `${size}px`,
                                                        height: `${size}px`,
                                                        transform:
                                                            "translate(-50%, -50%)",
                                                        background: `radial-gradient(circle, rgba(250,204,21,${0.45 + intensity * 0.5}) 0%, rgba(244,114,55,${0.25 + intensity * 0.4}) 40%, rgba(244,63,94,0) 75%)`,
                                                        mixBlendMode: "screen",
                                                    }}
                                                />
                                            );
                                        });
                                    })()}
                                </div>
                            ) : (
                                <Empty text="Najpierw wgraj obraz mapy w zakładce Mapa & POI." />
                            )}
                        </div>
                        <div className="lg:col-span-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-2">
                                Ranking POI
                            </div>
                            <ul className="space-y-2">
                                {stats.top_pois.slice(0, 12).map((p) => {
                                    const max = stats.top_pois[0].count || 1;
                                    return (
                                        <RankRow
                                            key={p.id}
                                            label={p.title || p.id}
                                            value={p.count}
                                            max={max}
                                        />
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {/* POI shares - how many times users pressed "Udostępnij" on POIs */}
            <div className="panel-yellow p-6" data-testid="stats-poi-shares">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <div className="label-eyebrow flex items-center gap-2">
                            <Share2 size={12} /> Udostępnienia POI
                        </div>
                        <div className="text-[11px] text-[color:var(--text-muted)] mt-1">
                            Łącznie:{" "}
                            <span className="text-[color:var(--primary)]">
                                {stats.poi_shares?.total ?? 0}
                            </span>
                            {" · "}Unikalni:{" "}
                            <span className="text-[color:var(--primary)]">
                                {stats.poi_shares?.unique ?? 0}
                            </span>
                            {" · "}7d: {stats.poi_shares?.last_7d ?? 0}
                            {" · "}30d: {stats.poi_shares?.last_30d ?? 0}
                        </div>
                    </div>
                </div>
                {(stats.top_shared_pois || []).length === 0 ? (
                    <Empty text={'Brak udostępnień POI - kliknięcia w przycisk „Udostępnij” na mapie pojawią się tutaj.'} />
                ) : (
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-2">
                            Najczęściej udostępniane POI
                        </div>
                        <ul className="space-y-2">
                            {stats.top_shared_pois.slice(0, 12).map((p) => {
                                const max = stats.top_shared_pois[0].count || 1;
                                return (
                                    <RankRow
                                        key={p.id}
                                        label={p.title || p.id}
                                        value={p.count}
                                        max={max}
                                    />
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>

            {/* Reports by status */}
            <div className="panel-yellow p-6">
                <div className="label-eyebrow mb-4">Zgłoszenia według statusu</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {["pending", "in_progress", "done", "cancelled"].map((st) => (
                        <div
                            key={st}
                            data-testid={`stats-report-${st}`}
                            className="p-4 rounded-md border"
                            style={{
                                background: "rgba(255,255,255,0.02)",
                                borderColor: `${REPORT_STATUS_COLORS[st]}44`,
                            }}
                        >
                            <div
                                className="text-[10px] uppercase tracking-[0.25em] mb-1"
                                style={{ color: REPORT_STATUS_COLORS[st] }}
                            >
                                {REPORT_STATUS_LABELS[st]}
                            </div>
                            <div className="text-2xl font-display text-white">
                                {reports.by_status?.[st] ?? 0}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Content overview */}
            <div className="panel-yellow p-6">
                <div className="label-eyebrow mb-4">Treść (przegląd)</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    <ContentCard icon={ImageIcon} label="Galeria" value={content.gallery} />
                    <ContentCard icon={ArchiveIcon} label="Archiwum" value={content.archive} />
                    <ContentCard icon={Puzzle} label="Dodatki" value={content.addons} />
                    <ContentCard icon={Users2} label="Zespół" value={content.team} />
                    <ContentCard icon={HelpCircle} label="FAQ" value={content.faq} />
                    <ContentCard icon={MapPin} label="POI na mapie" value={content.pois} />
                    <ContentCard icon={TagIcon} label="Kategorie POI" value={content.poi_categories} />
                    <ContentCard icon={Share2} label="Social" value={content.social} />
                </div>
            </div>

            <p className="text-[10px] text-[color:var(--text-muted)] text-center">
                Wygenerowano: {stats.generated_at?.slice(0, 19).replace("T", " ")}.
                Dane statystyk są w pełni anonimowe - adresy IP są hashowane (SHA-256 + sól), nie zapisujemy danych osobowych.
            </p>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, sub, color, testid, delta }) {
    return (
        <div
            data-testid={testid}
            className="panel-yellow p-5 flex items-start gap-4"
        >
            <div
                className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
                style={{ background: `${color}22`, color }}
            >
                <Icon size={18} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1 flex items-center gap-1.5">
                    <span>{label}</span>
                    {delta !== undefined && delta !== null && (
                        <DeltaPill value={delta} />
                    )}
                </div>
                <div className="font-display text-2xl text-white leading-none">
                    {Number(value || 0).toLocaleString("pl-PL")}
                </div>
                {sub && (
                    <div className="text-[11px] text-[color:var(--text-muted)] mt-1.5 truncate">
                        {sub}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Colored badge that shows a period-over-period % change (+12% / -3%). */
function DeltaPill({ value }) {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    const positive = num > 0;
    const negative = num < 0;
    const color = positive ? "#22c55e" : negative ? "#ef4444" : "#9ca3af";
    const Icon = positive ? ArrowUp : negative ? ArrowDown : Minus;
    return (
        <span
            className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9px] font-semibold normal-case tracking-normal"
            style={{ background: `${color}22`, color }}
            title="Zmiana vs poprzednie 7 dni"
        >
            <Icon size={9} strokeWidth={3} />
            {Math.abs(num).toFixed(1)}%
        </span>
    );
}

/** Horizontal-bar breakdown card (devices / browsers / OS). */
function BreakdownCard({ icon: Icon, title, rows, labelMap = {}, color, testid }) {
    const total = (rows || []).reduce((a, r) => a + (r.count || 0), 0);
    return (
        <div data-testid={testid} className="panel-yellow p-5">
            <div className="flex items-center gap-2 mb-4">
                <Icon size={14} className="text-[color:var(--primary)]" />
                <div className="text-[11px] uppercase tracking-[0.25em] text-white">
                    {title}
                </div>
                <span className="ml-auto text-[10px] text-[color:var(--text-muted)]">
                    n={total}
                </span>
            </div>
            {(!rows || rows.length === 0) ? (
                <Empty text="Brak danych" />
            ) : (
                <div className="space-y-2.5">
                    {rows.map((r) => {
                        const label = labelMap[r.key] || r.key;
                        const pct = total ? Math.round((r.count / total) * 100) : 0;
                        return (
                            <div key={r.key} className="group">
                                <div className="flex items-center justify-between text-xs mb-1">
                                    <span className="text-white capitalize">{label}</span>
                                    <span className="text-[color:var(--text-secondary)] tabular-nums">
                                        {r.count.toLocaleString("pl-PL")} · {pct}%
                                    </span>
                                </div>
                                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{ width: `${pct}%`, background: color }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/** Push subscribers panel with broadcast form. */
function PushPanel({ stats: pushStats, onSent }) {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [url, setUrl] = useState("/");
    const [sending, setSending] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const send = async () => {
        setSending(true);
        setFeedback(null);
        try {
            const { data } = await api.post("/push/send", {
                title: title.trim(),
                body: body.trim(),
                url: url.trim() || "/",
            });
            setFeedback({
                ok: true,
                text: `Wysłano do ${data.sent} subskrybentów (${data.failed} błędów, ${data.pruned} usunięto martwych).`,
            });
            setTitle("");
            setBody("");
            onSent?.();
        } catch (e) {
            setFeedback({
                ok: false,
                text: formatApiErrorDetail(e.response?.data?.detail) || e.message,
            });
        } finally {
            setSending(false);
        }
    };

    const count = pushStats?.total ?? 0;
    return (
        <div className="panel-yellow p-5" data-testid="push-panel">
            <div className="flex items-start gap-4">
                <div
                    className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: "#facc1522", color: "#facc15" }}
                >
                    <Bell size={18} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1">
                        Subskrybenci powiadomień push
                    </div>
                    <div className="font-display text-2xl text-white leading-none">
                        {count.toLocaleString("pl-PL")}
                    </div>
                    <div className="text-[11px] text-[color:var(--text-muted)] mt-1.5">
                        Nowi 7d: {pushStats?.last_7d ?? 0} · 30d:{" "}
                        {pushStats?.last_30d ?? 0}
                    </div>
                </div>
                <button
                    onClick={() => setOpen((v) => !v)}
                    disabled={count === 0}
                    className="btn-primary text-xs shrink-0 disabled:opacity-50"
                    data-testid="push-broadcast-toggle"
                >
                    <Send size={12} />
                    {open ? "Zwiń" : "Wyślij broadcast"}
                </button>
            </div>

            {open && (
                <div className="mt-5 pt-5 border-t border-white/10 space-y-3">
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            Tytuł
                        </span>
                        <input
                            className="input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="np. Nowa wersja moda!"
                            maxLength={100}
                            data-testid="push-broadcast-title"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            Treść
                        </span>
                        <textarea
                            className="input"
                            rows={3}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Krótki opis…"
                            maxLength={280}
                            data-testid="push-broadcast-body"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            URL po kliknięciu
                        </span>
                        <input
                            className="input"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="/"
                            data-testid="push-broadcast-url"
                        />
                    </label>

                    {feedback && (
                        <div
                            className={`text-xs px-3 py-2 rounded-md ${
                                feedback.ok
                                    ? "bg-emerald-500/10 text-emerald-300"
                                    : "bg-red-500/10 text-red-300"
                            }`}
                            data-testid="push-broadcast-feedback"
                        >
                            {feedback.text}
                        </div>
                    )}

                    <button
                        onClick={send}
                        disabled={sending || !title.trim()}
                        className="btn-primary w-full disabled:opacity-60"
                        data-testid="push-broadcast-send"
                    >
                        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        {sending ? "Wysyłam…" : `Wyślij do ${count} osób`}
                    </button>
                </div>
            )}
        </div>
    );
}

function ContentCard({ icon: Icon, label, value }) {
    return (
        <div
            className="p-3 rounded-md border border-white/5 bg-white/[0.02] flex items-center gap-3"
        >
            <Icon size={16} className="text-[color:var(--primary)]" />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] leading-none">
                    {label}
                </div>
                <div className="text-lg font-display text-white">{value ?? 0}</div>
            </div>
        </div>
    );
}

function RankRow({ label, value, max }) {
    const pct = max > 0 ? Math.max(4, (value / max) * 100) : 4;
    return (
        <li className="text-sm">
            <div className="flex items-center justify-between gap-3 mb-1">
                <span className="truncate text-[color:var(--text-secondary)]">
                    {label || "/"}
                </span>
                <span className="text-[color:var(--primary)] font-mono text-xs shrink-0">
                    {value}
                </span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                    className="h-full bg-[color:var(--primary)]/70"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </li>
    );
}

function Legend({ color, label }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-[color:var(--text-secondary)]">
            <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: color }}
            />
            {label}
        </span>
    );
}

function Empty({ text }) {
    return (
        <div className="text-center text-xs text-[color:var(--text-muted)] py-6">
            {text}
        </div>
    );
}

function prettyHost(url) {
    try {
        const u = new URL(url);
        return u.hostname + (u.pathname && u.pathname !== "/" ? u.pathname : "");
    } catch {
        return url || "";
    }
}

function formatPeakTs(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString("pl-PL", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function activeBucketLabel(seconds) {
    if (!seconds || seconds <= 60) return "minucie";
    if (seconds === 5 * 60) return "5-minutówce";
    if (seconds === 60 * 60) return "godzinie";
    if (seconds < 3600) return `${Math.round(seconds / 60)}-minutówce`;
    return `${Math.round(seconds / 3600)} godz.`;
}

function formatTick(iso, bucketSeconds) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (bucketSeconds >= 3600) {
            // 7d / 30d → day label
            return d.toLocaleString("pl-PL", { month: "2-digit", day: "2-digit", hour: "2-digit" });
        }
        // 1h / 6h / 24h → time only
        return d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso.slice(11, 16);
    }
}

function formatTooltipTs(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleString("pl-PL", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
